import { useEffect, useRef } from "react";

/**
 * In-app navigation guard for unsaved-work prompts.
 *
 * Every SPA navigation funnels through the single `navigate` from
 * {@link usePortalPath} (sidebar, command palette, topbar, page actions all
 * share that one instance), so a single module-level guard slot is enough to
 * intercept the lot without threading a blocker through the component tree.
 *
 * Only `navigate` (history pushes) is guarded — `replace` stays unguarded so
 * system redirects (legacy path rewrites) are never trapped behind a dialog.
 *
 * One guard at a time: only one editor with unsaved work is ever mounted. A
 * second registration replaces the first and is restored on unmount, so nested
 * mounts degrade gracefully rather than leaking a stale guard.
 */

export interface NavigationGuard {
  /** Whether navigation should be intercepted right now (e.g. form is dirty). */
  shouldBlock(): boolean;
  /**
   * Called when a navigation was intercepted. `proceed` performs the original
   * navigation, bypassing the guard — invoke it once the user confirms.
   */
  onBlocked(path: string, proceed: () => void): void;
}

let activeGuard: NavigationGuard | null = null;
// One-shot escape hatch: lets a guarded surface perform its own navigation
// (save-then-leave, revert-then-leave) without re-prompting through its own
// guard. Consumed by the next `consultNavigationGuard` call.
let bypassNextNavigation = false;

/**
 * Suppress the guard for the very next navigation. Call immediately before a
 * programmatic `navigate(...)` the guard would otherwise intercept.
 */
export function bypassNavigationGuardOnce(): void {
  bypassNextNavigation = true;
}

export function registerNavigationGuard(guard: NavigationGuard): () => void {
  const previous = activeGuard;
  activeGuard = guard;
  return () => {
    // Restore the prior guard only if we're still the installed one; a later
    // registration that already replaced us owns teardown of its own slot.
    if (activeGuard === guard) activeGuard = previous;
  };
}

/**
 * Consults the active guard for a pending navigation to `path`. Returns true
 * when the navigation was intercepted (the caller must not navigate); false
 * when it may proceed normally.
 */
export function consultNavigationGuard(path: string, proceed: () => void): boolean {
  if (bypassNextNavigation) {
    bypassNextNavigation = false;
    return false;
  }
  const guard = activeGuard;
  if (!guard?.shouldBlock()) return false;
  guard.onBlocked(path, proceed);
  return true;
}

/**
 * Consults the active guard for a browser Back/Forward traversal. Unlike an
 * in-app navigation, by the time `popstate` fires the browser has ALREADY
 * moved the URL — so an intercepted traversal must first rewind the address
 * bar (`restore`) before prompting. `restore` runs before `onBlocked` so a
 * guard that resolves synchronously (e.g. `window.confirm`) cannot have its
 * `proceed` clobbered by a restore that lands after it.
 */
export function consultNavigationGuardOnPopState(
  path: string,
  restore: () => void,
  proceed: () => void,
): boolean {
  if (bypassNextNavigation) {
    bypassNextNavigation = false;
    return false;
  }
  const guard = activeGuard;
  if (!guard?.shouldBlock()) return false;
  restore();
  guard.onBlocked(path, proceed);
  return true;
}

/** Test-only: clears the registry between cases. */
export function __resetNavigationGuardForTest(): void {
  activeGuard = null;
  bypassNextNavigation = false;
}

/**
 * Registers a navigation guard for the lifetime that `active` is true and
 * mirrors it with a `beforeunload` listener so tab close / reload also prompt.
 * `onBlocked` is read through a ref so the guard always calls the latest
 * closure without re-registering on every render.
 */
export function useNavigationGuard(
  active: boolean,
  onBlocked: (path: string, proceed: () => void) => void,
): void {
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;

  useEffect(() => {
    if (!active) return undefined;

    const unregister = registerNavigationGuard({
      shouldBlock: () => true,
      onBlocked: (path, proceed) => onBlockedRef.current(path, proceed),
    });

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      // The spec-compliant trio: preventDefault, assign returnValue, and return
      // a string. Browsers show their own generic copy; our string is ignored
      // but still required by some engines to trigger the prompt at all.
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    globalThis.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      unregister();
      globalThis.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [active]);
}
